// Eskom PARCC — inline banners (info / success / warning / danger) + the
// optimistic-concurrency conflict banner.
//
// Both themes render the same element and the same buttons; only the tokens
// differ. The banner replaces the pattern of surfacing a save conflict as a
// transient toast — a conflict needs a decision, and a decision needs to stay on
// screen until it is made.
import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';

export type BannerTone = 'info' | 'ok' | 'warn' | 'bad';

const TONE_ICON = {
  info: Info,
  ok: CheckCircle2,
  warn: AlertTriangle,
  bad: XCircle,
} as const;

const TONE_CLASS: Record<BannerTone, string> = {
  info: 'lm-banner info',
  ok: 'lm-banner ok',
  warn: 'lm-banner',
  bad: 'lm-banner bad',
};

export function Banner({
  tone = 'info',
  title,
  children,
  actions,
}: {
  tone?: BannerTone;
  title?: ReactNode;
  children?: ReactNode;
  /** Right-aligned controls (buttons). */
  actions?: ReactNode;
}): React.JSX.Element {
  const Icon = TONE_ICON[tone];
  return (
    <div className={TONE_CLASS[tone]} role={tone === 'bad' ? 'alert' : 'status'}>
      <Icon size={17} aria-hidden style={{ flex: 'none' }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        {title !== undefined && <strong style={{ display: 'block' }}>{title}</strong>}
        {children}
      </div>
      {actions && <div className="lm-banner-actions">{actions}</div>}
    </div>
  );
}

/**
 * The save-conflict banner. Someone else saved a newer version of the record
 * between this reader opening it and pressing save.
 *
 * RELOAD IS THE ONLY ANSWER OFFERED, DELIBERATELY. An earlier version of this
 * component also took an `onOverwrite` prop that rendered an "Overwrite anyway"
 * button behind a second confirmation. It was removed on 2026-07-24 — see
 * docs/concurrency-and-overwrite.md for the full decision.
 *
 * The short version: this app has NO write-side concurrency guard.
 * `UpdateTransactionData` updates by `id` alone, no mutation in the connector
 * carries a `rowVersion` precondition, and `lib/concurrency.ts` detects
 * conflicts client-side across two round-trips — so it narrows the
 * last-write-wins window without closing it. An "Overwrite anyway" button did
 * not therefore add a capability; it ADVERTISED an unguarded destructive path as
 * though it were sanctioned, in a tool whose whole purpose is establishing
 * defensibly who assessed what.
 *
 * Do NOT reintroduce an overwrite affordance here until the connector offers an
 * atomic compare-and-set and the prior version is preserved on supersede. At
 * that point the right control is a different thing — an explicit, attributable
 * re-base gated by the permission model — not this button returning.
 */
export function ConflictBanner({
  message = 'This record changed since you opened it.',
  detail = 'Another reviewer saved a newer version. Reload to pick up their changes, then reapply your edits.',
  onReload,
  busy = false,
}: {
  message?: ReactNode;
  detail?: ReactNode;
  onReload: () => void;
  busy?: boolean;
}): React.JSX.Element {
  return (
    <Banner
      tone="warn"
      title={message}
      actions={
        <button type="button" className="btn sm" onClick={onReload} disabled={busy}>
          Reload latest
        </button>
      }
    >
      <span className="small">{detail}</span>
    </Banner>
  );
}
